import { getRegionInfo } from "./utils.js";

/**
 * The result of a latency probe for a single AWS region.
 * Contains the region identity fields and the measured round-trip time.
 */
export interface LatencyResult {
  readonly id: string;
  readonly name: string;
  readonly latency: number;
}

/**
 * Ping the selected region to measure latency.
 *
 * Uses sequential DOM image error events to measure round-trip time.
 * Do NOT refactor to fetch() or XMLHttpRequest — CORS prevents it.
 *
 * @param selectedRegion - The AWS region ID to ping (e.g., "us-west-1").
 * @returns A promise resolving with region info and measured latency, or
 *   undefined if the region is unrecognised or an error occurs during setup.
 * @throws {Error} If an error occurs during the ping process.
 */
export default function pingRegion(selectedRegion: string): Promise<LatencyResult> | undefined {
  let addedElement: HTMLDivElement | null = null;
  try {
    const regionInfo = getRegionInfo(selectedRegion);
    if (!regionInfo) return undefined;
    const randomString = Math.floor(Math.random() * 0xfffffffffffff).toString(36);

    addedElement = document.createElement("div");
    addedElement.id = randomString;
    addedElement.className = "pingRegion";

    document.body.insertBefore(addedElement, document.body.firstChild);

    const imageCell = document.createElement("img");
    imageCell.id = `imageCell-${randomString}`;
    imageCell.style.display = "none";
    addedElement.appendChild(imageCell);

    const targetUrl = `${regionInfo.endpoint}ping?x=${randomString}`;

    const pingImageOne = document.createElement("img");
    pingImageOne.id = `pingImageOne-${randomString}`;
    pingImageOne.dataset.image = "1";
    pingImageOne.style.display = "none";
    imageCell.appendChild(pingImageOne);

    const pingImageTwo = document.createElement("img");
    pingImageTwo.id = `pingImageTwo-${randomString}`;
    pingImageTwo.dataset.image = "2";
    pingImageTwo.style.display = "none";
    imageCell.appendChild(pingImageTwo);

    return new Promise((resolve) => {
      pingImageOne.addEventListener("error", () => {
        const startTime = new Date().getTime();
        imageCell.innerHTML = "";
        imageCell.appendChild(pingImageTwo);

        pingImageTwo.addEventListener("error", () => {
          const endTime = new Date().getTime();
          const elapsed = endTime - startTime;
          resolve({ id: regionInfo.id, name: regionInfo.name, latency: elapsed });
          document.body.removeChild(addedElement as HTMLDivElement); // Remove the dynamically created element
        });

        pingImageTwo.setAttribute("src", targetUrl);
      });

      pingImageOne.setAttribute("src", targetUrl);
    });
  } catch (error) {
    if (addedElement && document.body.contains(addedElement)) {
      document.body.removeChild(addedElement); // Ensure cleanup in case of an error
    }
    console.error(error);
  }
}
